import { Fragment } from "react/jsx-runtime";
import InputField from "../inputfields/InputField";
import InputFieldForPhoneNumber from "../inputfields/InputFieldForPhoneNumber";
import Country from "./Country";
import InputfieldWithVerification from "../inputfields/InputfieldWithVerification";
import CustomDatePicker from "../DatePicker/CustomDatePicker";
import DropdownButtonForSearch from "../Dropdowns/DropdownButtonForSearch";
import dayjs from "dayjs";
import MultiPhoneNumbers from "./MultiPhoneNumbers";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";
import DynamicDropdown from "../Dropdowns/DynamicDropdown";
import CurrencyInputField from "../inputfields/InputFieldWithCurrency";
import NumberInputField from "../inputfields/NumberInputfield";

// Define the shape of an input field
interface InputFieldData {
  type: string;
  id: string;
  required: boolean;
  tabIndex: number;
  disabled: boolean;
  placeholder?: string;
  maxLength?: number;
  options?: string[];
  noSearch?: boolean;
  testId?: string;
  validation?: boolean;
  secondaryText?: string;
  percentage?: boolean,
  disabledDate?: boolean;
  bddTestId?: string;
  dropdownTestId?: string;
  bottom?: string;
  currency?: string;
  allowDecimal?: boolean;
  customWidth?: string;
  apiEndPoint?: string;
  handelDynamicValue?: (value: Record<string, any>) => void;
  onSearch?: (value: string) => void;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  hideSecondaryText?: boolean;
  width?: string;
  disabledPastDate?: boolean;
  note?: string;
}

// Define the structure of `inputs`
interface InputsStructure {
  [category: string]: {
    [fieldName: string]: InputFieldData;
  };
}
interface FlexibleErrors {
  [key: string]: string | any;
}

interface AddInputsProps {
  inputs: InputsStructure;
  totalInputCount: number;
  inputValues: Record<string, any>;
  onInputChange: (id: string, value: string) => void;
  handleVerify?: (id: string, resend: boolean) => void;
  felidError?: string[] | null;
  errors?: FlexibleErrors;
  handleSubmitOTP?: () => void;
  verifyLoaders?: Record<string, any>;
  disableAll?: boolean;
  editLoading?: boolean;
  isDisabledField?: string[] | null;
  mapping?: boolean;
  isOpenOtpModel?: boolean;
  setIsOpenOtpModel?: React.Dispatch<React.SetStateAction<boolean>>;
  index?: number;
  edit?: boolean;
  RequiredPostalCode?: boolean;
  showOnlyFirstField?: boolean;
  customWidth?: boolean;
  serviceFeeWidth?: boolean;
  loadMoreOptions?: (params: any) => void;
  isUpfrontFee?: boolean
}

export default function AddInputs({
  disableAll = false,
  errors,
  isDisabledField,
  inputs,
  inputValues,
  onInputChange,
  handleVerify = () => { },
  handleSubmitOTP = () => { },
  verifyLoaders = {},
  editLoading = false,
  felidError,
  mapping = false,
  isOpenOtpModel,
  setIsOpenOtpModel,
  index,
  edit,
  RequiredPostalCode,
  showOnlyFirstField = false,
  customWidth,
  serviceFeeWidth,
  loadMoreOptions,
  isUpfrontFee
}: AddInputsProps) {
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)
  const getFieldComp = (fieldData: InputFieldData, label: string) => {
    switch (fieldData.type) {
      case "input":
        return (
          <InputField
            inputItem={{
              labelName: label,
              id: fieldData.id,
            }}
            editLoading={editLoading}
            errorMessage={errors ? errors[fieldData.id] : ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange(fieldData.id, event.target.value)
            }
            value={inputValues[fieldData.id] || ""}
            required={fieldData.required}
            tabIndex={fieldData.tabIndex}
            fieldError={felidError?.includes(fieldData.id)}
            disabled={
              disableAll ||
              isDisabledField?.includes(fieldData.id) ||
              fieldData?.disabled
            }
            allowDecimal={fieldData?.allowDecimal || false}
            maxLength={fieldData.maxLength}
            maindivstyle={mapping ? "w-full" : customWidth ? "w-[30%]" : ""}
            className={mapping ? "w-full" : ""}
            testId={fieldData?.bddTestId ? `${fieldData?.bddTestId}-${index}` : fieldData?.testId ? fieldData?.testId : index !== undefined ? `${fieldData.id}-${index}` : ''}
            placeholder={fieldData?.placeholder ? translate(fieldData?.placeholder) : ''}
            secondaryText={fieldData?.secondaryText ? translate(fieldData?.secondaryText) : ''}
            percentage={fieldData?.percentage}
            customWidth={customWidth}
          />
        );
        case "numberInput":
        return (
          <NumberInputField
            inputItem={{
              labelName: label,
              id: fieldData.id,
            }}
            editLoading={editLoading}
            errorMessage={errors ? errors[fieldData.id] : ""}
            onChange={(id, value) => 
              onInputChange(id, value.toString())
            }
            value={inputValues[fieldData.id] || ""}
            required={fieldData.required}
            tabIndex={fieldData.tabIndex}
            fieldError={felidError?.includes(fieldData.id)}
            disabled={
              disableAll ||
              isDisabledField?.includes(fieldData.id) ||
              fieldData?.disabled
            }
            maxLength={fieldData.maxLength}
            maindivstyle={mapping ? "w-full" : serviceFeeWidth ? "w-[50%]" : customWidth ? "w-[30%]" : ""}
            className={mapping ? "w-full" : ""}
            testId={fieldData?.bddTestId ? `${fieldData?.bddTestId}-${index}` : fieldData?.testId ? fieldData?.testId : index !== undefined ? `${fieldData.id}-${index}` : ''}
            placeholder={fieldData?.placeholder ? translate(fieldData?.placeholder) : ''}
            secondaryText={fieldData?.secondaryText ? translate(fieldData?.secondaryText) : ''}
            percentage={fieldData?.percentage}
            min={fieldData?.min}
            max={fieldData?.max}
            decimalPlaces={fieldData?.decimalPlaces}
            customWidth={customWidth}
            hideSecondaryText={fieldData?.hideSecondaryText}
          />
        )
      case "date":
        return (
          <CustomDatePicker
            testID={fieldData?.bddTestId ? `${fieldData?.bddTestId}-${index}` : fieldData.id}
            label={label}
            handleStates={(value) =>
              onInputChange(
                fieldData.id,
                value ? value.format('YYYY-MM-DD') : '' // Convert Dayjs object to string
              )
            }
            id={fieldData.id}
            value={inputValues[fieldData.id] ? dayjs(inputValues[fieldData.id]) : null}
            error={errors ? errors[fieldData.id] : ""}
            disabled={fieldData.disabled || false}
            fieldError={felidError?.includes(fieldData.id)}
            type={fieldData?.type}
            divClassName={mapping ? 'w-full' : customWidth ? "w-[30%]" : 'w-80'}
            required={fieldData.required}
            editLoading={editLoading}
            disabledDate={fieldData?.disabledDate}
            disabledPastDate={fieldData?.disabledPastDate}
            note={fieldData?.note}
          />
        );
      case "phone_number":
        return (
          <InputFieldForPhoneNumber
            handleSubmitOTP={handleSubmitOTP}
            inputItem={{
              labelName: label,
              id: fieldData.id,
            }}
            editLoading={editLoading}
            value={inputValues[fieldData.id] || ""}
            errorMessage={errors ? errors[fieldData.id] : ""}
            onChange={onInputChange}
            required={fieldData.required}
            disabled={
              fieldData.disabled ||
              disableAll ||
              inputValues?.first_name?.length === 0 ||
              isDisabledField?.includes(fieldData.id)
            }
            handleVerify={handleVerify}
            fieldError={felidError?.includes(fieldData.id)}
            isVerified={inputValues[`${fieldData.id}_verify`]}
            ButtonLoading={verifyLoaders[fieldData.id]}
            isOpenOtpModel={isOpenOtpModel}
            setIsOpenOtpModel={setIsOpenOtpModel}
            verification={true}
            countryCode={inputValues.country_code}
            customClass={mapping ? "w-full" : ''}
            maxLength={fieldData.maxLength}
          />
        );
      case "phone_number_no_verify":
        return (
          <InputFieldForPhoneNumber
            inputItem={{
              labelName: label,
              id: fieldData.id,
            }}
            handleSubmitOTP={handleSubmitOTP}
            editLoading={editLoading}
            value={inputValues[fieldData.id] || ""}
            errorMessage={errors ? errors[fieldData.id] : ""}
            onChange={onInputChange}
            handleVerify={handleVerify}
            required={fieldData.required}
            fieldError={felidError?.includes(fieldData.id)}
            isVerified={inputValues[`${fieldData.id}_verify`]}
            customClass={`border borer-[#72latitude94C7] w-full h-[56px] rounded-[8px]`}
            verification={false}
            isOpenOtpModel={isOpenOtpModel}
            setIsOpenOtpModel={setIsOpenOtpModel}
          />
        );
      case "country_type":
      case "nationality":
        return (
          <Country
            inputValues={{
              country: inputValues.country,
              nationality: inputValues.nationality,
              province: inputValues.province,
              region: inputValues.region,
              district: inputValues.district,
              city_town:inputValues.city_town,
              ...(RequiredPostalCode && { postal_code: inputValues.postal_code }),
            }}
            labelName={label}
            id={fieldData?.id}
            tabIndex={fieldData.tabIndex}
            editLoading={editLoading}
            disableAll={disableAll}
            onInputChange={onInputChange}
            errorMessage={{
              country: errors && fieldData.type === 'country_type' ? (errors.country ? errors.country || '' : "") : errors && errors[fieldData.type] ? errors[fieldData.type] || '' : "",
              province: errors && errors.province ? errors.province : "",
              region: errors && errors.region ? errors.region : "",
              district: errors && errors.district ? errors.district : "",
              city_town: errors && errors.city_town ? errors.city_town : "",
              postal_code:
                errors && errors.postal_code ? errors.postal_code : "",
            }}
            mapping={mapping}
            type={fieldData.type}
            RequiredPostalCode={RequiredPostalCode}
          />
        );
      case "country_dropdown":
        return (
          <Country
            inputValues={{
              country: inputValues[fieldData?.id],
              nationality: inputValues[fieldData?.id],
              province: inputValues.province,
              region: inputValues.region,
              district: inputValues.district,
              ...(!RequiredPostalCode && { postal_code: inputValues.postal_code }),
            }}
            labelName={label}
            id={fieldData?.id}
            tabIndex={fieldData.tabIndex}
            editLoading={editLoading}
            disableAll={disableAll}
            onInputChange={onInputChange}
            errorMessage={{
              country: errors && errors[fieldData.id] ? errors[fieldData.id] || '' : "",
              province: errors && errors.province ? errors.province : "",
              region: errors && errors.region ? errors.region : "",
              district: errors && errors.district ? errors.district : "",
              postal_code:
                errors && errors.postal_code ? errors.postal_code : "",
            }}
            mapping={mapping}
            type={fieldData.type}
          />
        );
      case "input_verification":
        return (
          <InputfieldWithVerification
            handleSubmitOTP={handleSubmitOTP}
            inputItem={{
              labelName: label,
              id: fieldData.id,
            }}
            editLoading={editLoading}
            errorMessage={errors ? errors[fieldData.id] : ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange(fieldData.id, event.target.value)
            }
            maxLength={fieldData.maxLength}
            value={inputValues[fieldData.id] || ""}
            required={fieldData.required}
            handleVerify={handleVerify}
            disabled={
              fieldData.disabled ||
              disableAll ||
              isDisabledField?.includes(fieldData.id)
            }
            tabIndex={fieldData.tabIndex}
            isVerified={inputValues[`${fieldData.id}_verify`]}
            ButtonLoading={verifyLoaders[fieldData.id]}
            isOpenOtpModel={isOpenOtpModel}
            setIsOpenOtpModel={setIsOpenOtpModel}
            validation={fieldData?.validation}
            mapping={mapping}
          />
        );
      case "dropDown":
        return (
          <DropdownButtonForSearch
            options={fieldData?.options || []}
            labelName={label}
            editLoading={editLoading}
            id={fieldData?.id}
            tabIndex={fieldData?.tabIndex}
            noSearch={fieldData?.noSearch}
            // className={fieldData?.className}
            onChange={onInputChange}
            selectedValue={inputValues[fieldData.id]}
            required={fieldData?.required}
            errorMessage={errors && errors[fieldData?.id] ? errors[fieldData?.id] : ''}
            disabled={fieldData?.disabled}
            width={fieldData.width || ''}
            modalStyle={mapping ? '' : serviceFeeWidth ? 'w-[50%]' : customWidth ? 'w-[30%]' : 'w-full'}
            className={mapping ? 'w-full' : customWidth ? 'w-full' : ''}
            customWidth={customWidth}
            // setSelectedOption={setSelectedOption}
            loadMoreOptions={loadMoreOptions}
            testId={fieldData?.testId ? fieldData?.testId : index !== undefined ? `${fieldData.id}-${index}` : ''}
            setSearchedAccount={fieldData.onSearch}
            dropdownTestId={fieldData?.dropdownTestId}
            placeholder={fieldData?.placeholder}
          />
        );
      case "dynamic_dropDown":
        return (
          <DynamicDropdown
            apiEndPoint={fieldData?.apiEndPoint}
            labelName={label}
            editLoading={editLoading}
            id={fieldData?.id}
            tabIndex={fieldData?.tabIndex}
            noSearch={fieldData?.noSearch}
            // className={fieldData?.className}
            handelDynamicValue={fieldData?.handelDynamicValue}
            onChange={onInputChange}
            selectedValue={inputValues[fieldData.id]}
            required={fieldData?.required}
            errorMessage={errors && errors[fieldData?.id] ? errors[fieldData?.id] : ''}
            disabled={fieldData?.disabled}
            modalStyle={mapping ? ' mb-5' : customWidth ? 'w-[30%]' : ''}
            className={mapping ? 'w-full' : customWidth ? 'w-full' : ''}
            customWidth={customWidth}
            // setSelectedOption={setSelectedOption}
            loadMoreOptions={loadMoreOptions}
            testId={fieldData?.testId ? fieldData?.testId : index !== undefined ? `${fieldData.id}-${index}` : ''}
            setSearchedAccount={fieldData.onSearch}
            dropdownTestId={fieldData?.dropdownTestId}
          />
        );
      case 'multiPhoneNumber':
        return (
          <MultiPhoneNumbers
            handleVerify={handleVerify}
            handleSubmitOTP={handleSubmitOTP}
            disabled={
              fieldData.disabled ||
              disableAll ||
              inputValues.first_name.length === 0 ||
              isDisabledField?.includes(fieldData.id)
            }
            maxLength={fieldData.maxLength}
            isOpenOtpModel={isOpenOtpModel}
            setIsOpenOtpModel={setIsOpenOtpModel}
            edit={edit || false}
            editLoading={editLoading}
            mapping={mapping}
          />);
      case 'currency':
        return (
          <CurrencyInputField
            inputItem={{
              labelName: label,
              id: fieldData.id
            }}
            value={inputValues[fieldData.id]}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange(fieldData.id, event.target.value)
            }
            customWidth='w-full'
            tabIndex={0}
            required={fieldData.required}
            disabled={
              fieldData?.disabled
            }
            testId={fieldData?.bddTestId ? `${fieldData?.bddTestId}-${index}` : fieldData?.testId ? fieldData?.testId : index !== undefined ? `${fieldData.id}-${index}` : ''}
            errorMessage={errors ? errors[fieldData.id] : ""}
            editLoading={editLoading}
            min={fieldData?.min}
            max={fieldData?.max}
          />
        )
      default:
        return null; // Gracefully handle unknown types
    }
  };


  return (
    <>
      {Object.keys(inputs).map((inputCategory, categoryIndex) => (
        <div className={`gap-4 ${customWidth ? 'w-full' : ''}`} data-testid="add-inputs" key={categoryIndex} dir={notEnglish ? "rtl" : "ltr"}>
          {inputCategory !== "" && (
            <h1
              className={`text-heading3 text-primary_text underline ${categoryIndex !== 0 ? "pt-8 pb-4" : "py-4"
                }`}
            >
              {translate(inputCategory)}
            </h1>
          )}
          {!mapping ? (
            <div className={`flex gap-x-10 gap-y-4 ${customWidth ? '' : 'flex-wrap'}`}>
              {Object.keys(inputs[inputCategory]).map(
                (fieldName, fieldIndex) => (
                  <Fragment key={`${categoryIndex}-${fieldIndex}`}>
                    {(!showOnlyFirstField || fieldIndex === 0) && getFieldComp(inputs[inputCategory][fieldName], fieldName)}
                  </Fragment>
                )
              )}
            </div>
          ) : (
            <div className="-mx-5 flex flex-wrap">
              {Object.keys(inputs[inputCategory]).map((fieldName, fieldIndex) =>
                (!showOnlyFirstField || fieldIndex === 0) &&
                (inputs[inputCategory][fieldName]["type"] === "country_type" ? (
                  getFieldComp(inputs[inputCategory][fieldName], fieldName)
                ) : inputs[inputCategory][fieldName]['type'] === "multiPhoneNumber" ? (
                  getFieldComp(inputs[inputCategory][fieldName], fieldName)
                ) : (
                  <div className={`px-5 ${isUpfrontFee ? 'w-full' : 'w-1/3'} ${inputs[inputCategory][fieldName]["bottom"] ? inputs[inputCategory][fieldName]["bottom"] : 'mb-5'}  h-full`} key={fieldIndex}>
                    {getFieldComp(inputs[inputCategory][fieldName], fieldName)}
                  </div>
                ))
              )}
            </div>

          )}
        </div>
      ))}
    </>
  );
}